/**
 * Provider-agnostic frontend error reporting (SPEC_04 Task 4).
 *
 * NO-OP unless `VITE_ERROR_DSN` is configured — then each report is a
 * fire-and-forget JSON POST to that endpoint. Swapping in Sentry/Highlight
 * later means replacing `send()` only.
 *
 * SCRUBBING CONTRACT — this app handles privileged attorney work product,
 * and the reporting path must never become an exfiltration channel:
 *   - never include document contents, chat message bodies, or artifact
 *     payloads — only `Error.message`/stack and coarse context make it in;
 *   - bearer tokens / JWTs / long base64-ish runs are masked out of every
 *     string field before send;
 *   - everything is length-capped.
 * Callers must not stuff content into `extra` — ids, counts, and step
 * names only.
 *
 * Consumers: ErrorBoundary (componentDidCatch reports automatically), the
 * chat store's SSE failure paths, and the global window handlers installed
 * by `installGlobalErrorReporting()` in main.tsx (boundaries don't catch
 * event-handler/async errors — the globals cover those).
 */

export type ErrorReportContext = {
  /** Where the report came from: 'error-boundary' | 'sse' | 'window' | 'unhandledrejection' | custom. */
  source: string;
  /** Route path at the time of the error (no query string — may carry ids, keep coarse). */
  route?: string;
  /** Server correlation ref when one is known (joins to edge_function_errors). */
  requestId?: string;
  /** Coarse, content-free extras (ids, counts, step names). */
  extra?: Record<string, string | number | boolean>;
};

const MAX_FIELD_CHARS = 2000;

/** Mask token-shaped material and cap length. Exported for tests. */
export function scrub(text: string): string {
  return (
    text
      // Authorization header / bearer values.
      .replace(/Bearer\s+[A-Za-z0-9._~+/=-]+/gi, 'Bearer [redacted]')
      // JWT-shaped triples.
      .replace(/\b[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\b/g, '[redacted-jwt]')
      // Long base64-ish runs (keys, blobs, inline documents).
      .replace(/[A-Za-z0-9+/=_-]{64,}/g, '[redacted-blob]')
      .slice(0, MAX_FIELD_CHARS)
  );
}

function dsn(): string {
  return (import.meta.env.VITE_ERROR_DSN as string | undefined)?.trim() ?? '';
}

export function reportError(error: unknown, context: ErrorReportContext): void {
  const endpoint = dsn();
  if (!endpoint) return; // no-op by default

  try {
    const err = error instanceof Error ? error : null;
    const payload = {
      message: scrub(err?.message ?? String(error)),
      stack: err?.stack ? scrub(err.stack) : null,
      name: err?.name ?? typeof error,
      source: context.source,
      route: context.route ?? (typeof window !== 'undefined' ? window.location.pathname : null),
      request_id: context.requestId ?? null,
      extra: context.extra ?? null,
      ts: new Date().toISOString(),
    };
    const body = JSON.stringify(payload);
    // sendBeacon survives unloads; fetch keepalive is the fallback. Both
    // fire-and-forget — reporting must never affect the app.
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      navigator.sendBeacon(endpoint, body);
    } else {
      void fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    // Reporting can never throw into the app.
  }
}

/**
 * Install window-level handlers for the classes error boundaries can't
 * catch (event handlers, async code). Call once from main.tsx.
 */
export function installGlobalErrorReporting(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('error', (event) => {
    reportError(event.error ?? event.message, { source: 'window' });
  });
  window.addEventListener('unhandledrejection', (event) => {
    reportError(event.reason, { source: 'unhandledrejection' });
  });
}
